/* Galatée — composants interactifs (Web Components natifs, sans librairie).
   Chargé en module différé depuis layout/theme.liquid. Chaque composant fonctionne sans JS en repli :
   les signes sont des liens, les filtres un formulaire GET, le panier garde le comportement d'Horizon. */

const MOTION_OK = () => !window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/* ---------- Signe à partir d'une date (bornes « MM-JJ », signe à cheval sur l'année géré) ---------- */
function mdOf(date) {
  return String(date.getMonth() + 1).padStart(2, '0') + '-' + String(date.getDate()).padStart(2, '0');
}
function inRange(md, start, end) {
  return start <= end ? md >= start && md <= end : md >= start || md <= end;
}
/** Renvoie les handles de signe pour une date ; deux signes si la date est à ±1 jour d'une bascule. */
function signsForDate(value, items) {
  if (!value) return [];
  const [y, m, d] = value.split('-').map(Number);
  if (!y || !m || !d) return [];
  const day = new Date(y, m - 1, d);
  const found = [];
  for (const offset of [0, -1, 1]) {
    const probe = new Date(day);
    probe.setDate(day.getDate() + offset);
    const md = mdOf(probe);
    for (const el of items) {
      if (inRange(md, el.dataset.start, el.dataset.end) && !found.includes(el.dataset.galSign || el.dataset.galPanel || el.dataset.galGiftSign)) {
        found.push(el.dataset.galSign || el.dataset.galPanel || el.dataset.galGiftSign);
      }
    }
  }
  return found.slice(0, 2);
}
function refocus(el) {
  if (!el || !MOTION_OK()) return;
  el.classList.remove('is-entering');
  void el.offsetWidth;
  el.classList.add('is-entering');
}

/* ---------- « Quelle est votre pierre ? » ---------- */
class GalSignFinder extends HTMLElement {
  connectedCallback() {
    this.chips = [...this.querySelectorAll('[data-gal-sign]')];
    this.panels = [...this.querySelectorAll('[data-gal-panel]')];
    this.cusp = this.querySelector('[data-gal-cusp]');
    this.chips.forEach((chip) => {
      chip.addEventListener('click', (e) => {
        e.preventDefault();
        this.select(chip.dataset.galSign);
        if (this.cusp) this.cusp.hidden = true;
      });
      chip.addEventListener('keydown', (e) => {
        if (e.key === ' ') { e.preventDefault(); chip.click(); }
      });
    });
    const birth = this.querySelector('[data-gal-birth]');
    birth?.addEventListener('change', () => {
      const signs = signsForDate(birth.value, this.chips);
      if (!signs.length) return;
      this.select(signs[0]);
      if (this.cusp) {
        if (signs.length > 1) {
          const names = signs.map((h) => this.chips.find((c) => c.dataset.galSign === h)?.dataset.name);
          this.cusp.textContent = `Votre date est à la frontière de deux signes : ${names.join(' ou ')}. Les deux pierres vous vont.`;
          this.cusp.hidden = false;
        } else {
          this.cusp.hidden = true;
        }
      }
    });
    const tags = this.querySelector('[data-gal-lead-tags]');
    this.tagsInput = tags;
  }
  select(handle) {
    this.chips.forEach((c) => c.setAttribute('aria-pressed', String(c.dataset.galSign === handle)));
    this.panels.forEach((p) => {
      const on = p.dataset.galPanel === handle;
      p.hidden = !on;
      if (on) refocus(p);
    });
    if (this.tagsInput) this.tagsInput.value = `newsletter,signe,signe-${handle}`;
  }
}

/* ---------- Date de naissance → signe(s) ---------- */
class GalBirthdateFinder extends HTMLElement {
  connectedCallback() {
    this.panels = [...this.querySelectorAll('[data-gal-panel]')];
    this.cusp = this.querySelector('[data-gal-cusp]');
    const input = this.querySelector('[data-gal-birth]');
    input?.addEventListener('change', () => {
      const signs = signsForDate(input.value, this.panels);
      this.panels.forEach((p) => {
        p.hidden = !signs.includes(p.dataset.galPanel);
        if (!p.hidden) refocus(p);
      });
      if (this.cusp) {
        this.cusp.hidden = signs.length < 2;
        this.cusp.textContent = this.dataset.cuspText || '';
      }
    });
  }
}

/* ---------- Trouver un cadeau : signe × budget, filtrage local ---------- */
class GalGiftFinder extends HTMLElement {
  connectedCallback() {
    this.items = [...this.querySelectorAll('.gal-gift__item')];
    this.signChips = [...this.querySelectorAll('[data-gal-gift-sign]')];
    this.budgetChips = [...this.querySelectorAll('[data-gal-gift-budget]')];
    this.grid = this.querySelector('[data-gal-gift-grid]');
    this.empty = this.querySelector('[data-gal-gift-empty]');
    this.sign = '';
    this.budget = '';
    this.signChips.forEach((c) => c.addEventListener('click', () => { this.sign = c.dataset.galGiftSign; this.press(this.signChips, c); this.apply(); }));
    this.budgetChips.forEach((c) => c.addEventListener('click', () => { this.budget = c.dataset.galGiftBudget; this.press(this.budgetChips, c); this.apply(); }));
    const birth = this.querySelector('[data-gal-birth]');
    birth?.addEventListener('change', () => {
      const signs = signsForDate(birth.value, this.signChips.filter((c) => c.dataset.start));
      if (!signs.length) return;
      const chip = this.signChips.find((c) => c.dataset.galGiftSign === signs[0]);
      chip?.click();
    });
  }
  press(group, active) { group.forEach((c) => c.setAttribute('aria-pressed', String(c === active))); }
  apply() {
    const [min, max] = this.budget ? this.budget.split('-').map(Number) : [0, Infinity];
    let shown = 0;
    this.items.forEach((it) => {
      const price = it.dataset.price === '' ? null : Number(it.dataset.price);
      const ok = (!this.sign || it.dataset.sign === this.sign) && (price === null || (price >= min && price <= max));
      it.hidden = !ok;
      if (ok) shown++;
    });
    if (this.empty) this.empty.hidden = shown > 0;
    refocus(this.grid);
  }
}

/* ---------- Fiche produit : taille obligatoire + avertissement bébé ---------- */
class GalSizeGate extends HTMLElement {
  connectedCallback() {
    if (Number(this.dataset.optionIndex) < 0) return;
    this.optionName = (this.dataset.optionName || 'taille').toLowerCase();
    this.baby = (this.dataset.baby || 'bébé').toLowerCase();
    this.label = this.dataset.label || 'Choisissez votre taille';
    this.scope = this.closest('.shopify-section') || document;
    this.chosen = false;
    this.safety = this.querySelector('[data-gal-safety]');
    this.onChange = (e) => {
      const t = e.target;
      if (!(t instanceof HTMLInputElement || t instanceof HTMLSelectElement)) return;
      const name = (t.dataset.optionName || t.name || '').toLowerCase();
      if (!name.includes(this.optionName)) return;
      this.chosen = true;
      const value = (t instanceof HTMLSelectElement ? t.selectedOptions[0]?.value : t.value) || '';
      if (this.safety) this.safety.hidden = !value.toLowerCase().includes(this.baby);
      this.apply();
    };
    this.onClick = (e) => {
      if (this.chosen) return;
      const btn = e.target.closest?.('[ref="addToCartButton"], .sticky-add-to-cart button[type="submit"]');
      if (!btn) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      const field = this.scope.querySelector(`[data-option-name]`);
      const fieldset = [...this.scope.querySelectorAll('[data-option-name]')].find((i) => (i.dataset.optionName || '').toLowerCase().includes(this.optionName));
      (fieldset?.closest('fieldset') || fieldset || field)?.scrollIntoView({ behavior: MOTION_OK() ? 'smooth' : 'auto', block: 'center' });
      fieldset?.focus({ preventScroll: true });
    };
    this.scope.addEventListener('change', this.onChange, true);
    document.addEventListener('click', this.onClick, true);
    this.observer = new MutationObserver(() => this.apply());
    this.observer.observe(this.scope, { childList: true, subtree: true });
    this.apply();
  }
  disconnectedCallback() {
    this.scope?.removeEventListener('change', this.onChange, true);
    document.removeEventListener('click', this.onClick, true);
    this.observer?.disconnect();
  }
  apply() {
    const buttons = [...this.scope.querySelectorAll('[ref="addToCartButton"]'), ...document.querySelectorAll('.sticky-add-to-cart button[type="submit"]')];
    this.observer?.disconnect();
    buttons.forEach((btn) => {
      const text = btn.querySelector('.add-to-cart-text__content, [ref="addToCartTextContent"]') || btn;
      if (!this.chosen) {
        if (!btn.dataset.galGated) {
          btn.dataset.galGated = 'true';
          btn.dataset.galOriginal = text.textContent.trim();
        }
        btn.setAttribute('aria-disabled', 'true');
        if (text.textContent.trim() !== this.label) text.textContent = this.label;
      } else if (btn.dataset.galGated) {
        delete btn.dataset.galGated;
        btn.removeAttribute('aria-disabled');
        if (btn.dataset.galOriginal) text.textContent = btn.dataset.galOriginal;
      }
    });
    this.observer?.observe(this.scope, { childList: true, subtree: true });
  }
}

/* ---------- Jauge livraison offerte (mise à jour après ajout au panier) ---------- */
class GalShippingGauge extends HTMLElement {
  connectedCallback() {
    // Événement standard Shopify émis par Horizon à chaque modification des lignes du panier
    this.onLines = (e) => {
      const done = e?.promise || e?.detail?.promise;
      if (done && typeof done.then === 'function') done.then(() => this.refresh(), () => this.refresh());
      else setTimeout(() => this.refresh(), 600);
    };
    document.addEventListener('shopify:cart:lines-update', this.onLines);
  }
  disconnectedCallback() {
    document.removeEventListener('shopify:cart:lines-update', this.onLines);
  }
  async refresh() {
    try {
      const cart = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`).then((r) => r.json());
      const threshold = Number(this.dataset.threshold);
      const left = threshold - cart.total_price;
      const pct = Math.min(100, Math.round((cart.total_price * 100) / threshold));
      const text = this.querySelector('[data-gal-gauge-text]');
      const bar = this.querySelector('[data-gal-gauge-bar]');
      const money = (cents) => (cents / 100).toLocaleString(document.documentElement.lang || 'fr', { style: 'currency', currency: cart.currency });
      if (text) text.textContent = left > 0 ? this.dataset.left.replace('[montant]', money(left)) : this.dataset.done;
      bar?.style.setProperty('--pct', `${pct}%`);
    } catch (e) { /* la jauge garde sa valeur rendue côté serveur */ }
  }
}

/* ---------- Livraison estimée (jours ouvrés, fermeture atelier) ---------- */
class GalEta extends HTMLElement {
  connectedCallback() {
    const addBusinessDays = (start, n) => {
      const d = new Date(start);
      let left = n;
      while (left > 0) {
        d.setDate(d.getDate() + 1);
        if (d.getDay() !== 0 && d.getDay() !== 6) left--;
      }
      return d;
    };
    let start = new Date();
    const closed = this.dataset.closed && /^\d{4}-\d{2}-\d{2}$/.test(this.dataset.closed) ? new Date(this.dataset.closed) : null;
    if (closed && closed > start) start = closed;
    const fmt = new Intl.DateTimeFormat('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' });
    const a = addBusinessDays(start, Number(this.dataset.min) || 3);
    const b = addBusinessDays(start, Number(this.dataset.max) || 6);
    const out = this.querySelector('[data-gal-eta]');
    if (out) out.textContent = `entre le ${fmt.format(a)} et le ${fmt.format(b)}`;
  }
}

customElements.get('gal-sign-finder') || customElements.define('gal-sign-finder', GalSignFinder);
customElements.get('gal-birthdate-finder') || customElements.define('gal-birthdate-finder', GalBirthdateFinder);
customElements.get('gal-gift-finder') || customElements.define('gal-gift-finder', GalGiftFinder);
customElements.get('gal-size-gate') || customElements.define('gal-size-gate', GalSizeGate);
customElements.get('gal-shipping-gauge') || customElements.define('gal-shipping-gauge', GalShippingGauge);
customElements.get('gal-eta') || customElements.define('gal-eta', GalEta);

/* ---------- Barre d'achat fixe (après le premier écran, masquée près du pied de page) ---------- */
class GalStickyCta extends HTMLElement {
  connectedCallback() {
    this.hidden = false;
    const offset = Number(this.dataset.offset) || 500;
    const footer = document.querySelector('footer');
    let nearFooter = false;
    if (footer && 'IntersectionObserver' in window) {
      new IntersectionObserver((entries) => { nearFooter = entries[0].isIntersecting; this.update(offset, nearFooter); }).observe(footer);
    }
    this.onScroll = () => this.update(offset, nearFooter);
    window.addEventListener('scroll', this.onScroll, { passive: true });
    this.update(offset, nearFooter);
  }
  disconnectedCallback() { window.removeEventListener('scroll', this.onScroll); }
  update(offset, nearFooter) {
    const y = window.scrollY || document.documentElement.scrollTop;
    this.classList.toggle('is-visible', y > offset && !nearFooter);
  }
}
customElements.get('gal-sticky-cta') || customElements.define('gal-sticky-cta', GalStickyCta);

/* ---------- Cartes produit : pastilles de cordon (photo + prix + lien) ---------- */
document.addEventListener('click', (e) => {
  const btn = e.target.closest?.('[data-gal-cord]');
  if (!btn) return;
  const card = btn.closest('.gal-card');
  card.querySelectorAll('[data-gal-cord]').forEach((b) => b.setAttribute('aria-pressed', String(b === btn)));
  const img = card.querySelector('[data-gal-card-img]');
  if (img && btn.dataset.img) { img.removeAttribute('srcset'); img.src = btn.dataset.img; }
  const price = card.querySelector('[data-gal-card-price]');
  if (price && btn.dataset.price) price.textContent = btn.dataset.price;
  if (btn.dataset.url) card.querySelectorAll('a.gal-card__link, .gal-card__title a').forEach((a) => (a.href = btn.dataset.url));
});

/* ---------- Filtres de collection : envoi au changement ---------- */
document.addEventListener('change', (e) => {
  const form = e.target.closest?.('form[data-gal-autosubmit]');
  if (!form || e.target.type === 'number') return;
  form.requestSubmit ? form.requestSubmit() : form.submit();
});

/* ---------- Panier : « C'est un cadeau » + message (attribut de commande) ---------- */
let giftTimer;
async function saveGift(root) {
  const isGift = root.querySelector('[data-gal-gift-toggle]')?.checked;
  const msg = root.querySelector('[data-gal-gift-message]');
  const attributes = { 'Cadeau': isGift ? 'Oui' : '', 'Message cadeau': isGift && msg ? msg.value.slice(0, Number(msg.maxLength) || 200) : '' };
  try {
    await fetch(`${window.Shopify?.routes?.root || '/'}cart/update.js`, {
      method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ attributes })
    });
  } catch (err) { /* sans effet bloquant : la commande reste possible */ }
}
document.addEventListener('change', (e) => {
  const root = e.target.closest?.('[data-gal-cart-extras]');
  if (!root) return;
  if (e.target.matches('[data-gal-gift-toggle]')) {
    const box = root.querySelector('.gal-cart-gift');
    if (box) box.hidden = !e.target.checked;
  }
  saveGift(root);
});
document.addEventListener('input', (e) => {
  const root = e.target.closest?.('[data-gal-cart-extras]');
  if (!root || !e.target.matches('[data-gal-gift-message]')) return;
  const count = root.querySelector('[data-gal-gift-count]');
  if (count) count.textContent = `${e.target.value.length}/${e.target.maxLength}`;
  clearTimeout(giftTimer);
  giftTimer = setTimeout(() => saveGift(root), 600);
});
